import { useState } from 'react';
import usePopoverEdgeAlign from '@/hooks/usePopoverEdgeAlign';
import { MdAdd } from 'react-icons/md';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import Checkbox from '@/components/Checkbox';
import { Button } from '@/components/Button';
import { Tag, type TagColor } from '@/components/Tag';
import useProvidedJobPostTags from '../hooks/useProvidedJobPostTags';
import {
  useProvidedJobPostChain,
  useProvidedJobPostUnchain,
} from '../hooks/useProvidedJobPostTagMutation';
import type { CustomTag } from '../../custom_tags/hooks/useCustomTags';

const POPOVER_WIDTH = 250;

interface Props {
  id: number;
  tags: CustomTag[];
}

export default function TagSelectPopover({ id, tags }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(tags.map((t) => t.id));
  const { triggerRef, align, updateAlign } = usePopoverEdgeAlign(POPOVER_WIDTH);

  const { data } = useProvidedJobPostTags();
  const { mutate: mutateChain } = useProvidedJobPostChain();
  const { mutate: mutateUnchain } = useProvidedJobPostUnchain();

  const handleTagToggle = (tagId: number, checked: boolean) => {
    if (checked) {
      setSelectedTagIds((prev) => [...prev, tagId]);
      mutateChain(
        { tagId, providedJobPostId: id },
        {
          onError: () => {
            setSelectedTagIds((prev) => prev.filter((id) => id !== tagId));
          },
        }
      );
    } else {
      setSelectedTagIds((prev) => prev.filter((id) => id !== tagId));
      mutateUnchain(
        { tagId, providedJobPostId: id },
        {
          onError: () => {
            setSelectedTagIds((prev) => [...prev, tagId]);
          },
        }
      );
    }
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        if (open) updateAlign();
        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <Button ref={triggerRef} color="secondary" size="sm">
          <MdAdd className="size-icon-sm" />
          タグを追加
        </Button>
      </PopoverTrigger>
      {data?.tags && (
        <PopoverContent
          side="bottom"
          align={align}
          sideOffset={4}
          collisionPadding={8}
          className="w-max min-w-[250px] rounded p-0"
        >
          <div className="flex flex-col">
            <div className="flex max-h-[225px] flex-col overflow-y-auto">
              {data.tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex h-10 shrink-0 cursor-pointer items-center px-3 hover:bg-gray"
                >
                  <Checkbox
                    value={tag.id}
                    checked={selectedTagIds.includes(tag.id)}
                    onChange={(checked) => handleTagToggle(tag.id, checked)}
                  >
                    <Tag size="sm" color={tag.colorHex as TagColor}>
                      {tag.name}
                    </Tag>
                  </Checkbox>
                </div>
              ))}
            </div>
            <div className="border-0 border-t border-solid border-border px-3 py-2">
              <a href="/agent/job_post_tags" target="_blank">
                <Button color="tertiary" size="sm" className="w-[96px] justify-start">
                  <i className="fas fa-cog text-sm" />
                  <span>タグを管理</span>
                </Button>
              </a>
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}
